import { useState } from 'react'
import axios from 'axios'
import { ScrollText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import {
  useExportMachineryDirectiveDeclarationOfConformityPdf,
  useGenerateDeclarationOfConformity,
  useGetMachineryDirectiveCompliance,
  usePatchDeclarationOfConformityMarkdown,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { DocumentEditor } from '@/components/document-editor'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { DeclarationOfConformityRequirementsChecklist } from '../../components/DeclarationOfConformityRequirementsChecklist'
import { ComplianceDocumentManifestProvider } from '../../context/ComplianceDocumentManifestContext'
import { useDeclarationOfConformityRequirements } from '../../hooks/useDeclarationOfConformityRequirements'
import { buildMachineryDirectiveStudioBaseHref } from '../../machineryDirectiveStudioPaths'
import { isComplianceDocumentStaleAfterClassificationChange } from '../../utils/isComplianceDocumentStaleAfterClassificationChange'

export function DeclarationOfConformityPage() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const productId = params.productId ?? ''
  const complianceId = params.machineryDirectiveComplianceId ?? ''
  const studioBaseHref = buildMachineryDirectiveStudioBaseHref(
    productId,
    complianceId
  )
  const buildHref = (path = '') => `${studioBaseHref}${path ? `/${path}` : ''}`
  const { t, i18n } = useTranslation('machineryDirectiveCompliance')

  const { data: compliance, isLoading } =
    useGetMachineryDirectiveCompliance(complianceId)
  const {
    requirements,
    hasManufacturer,
    hasAuthorisedRepresentative,
    hasAtLeastOneStandardAssigned,
    isComplianceStudioComplete,
  } = useDeclarationOfConformityRequirements(complianceId, productId)

  const generateDoc = useGenerateDeclarationOfConformity(complianceId)
  const patchMarkdown = usePatchDeclarationOfConformityMarkdown(complianceId)
  const exportPdf =
    useExportMachineryDirectiveDeclarationOfConformityPdf(complianceId)

  const [regenerateOpen, setRegenerateOpen] = useState(false)
  const [declarationMarkdownSyncKey, setDeclarationMarkdownSyncKey] =
    useState(0)

  const isPartlyCompletedMachine = compliance?.isPartlyCompletedMachine === true
  const pageKeys = isPartlyCompletedMachine
    ? 'declarationOfIncorporationPage'
    : 'declarationOfConformityPage'
  const storedMarkdown = compliance?.declarationOfConformityMarkdown?.trim()
  const displayMarkdown = compliance?.declarationOfConformityMarkdown ?? ''
  const hasDocument = (displayMarkdown?.trim().length ?? 0) > 0
  const showClassificationStaleWarning =
    hasDocument &&
    isComplianceDocumentStaleAfterClassificationChange(
      compliance?.isPartlyCompletedMachineUpdatedAt,
      compliance?.declarationOfConformityUpdatedAt
    )

  const canGenerate =
    hasManufacturer &&
    hasAuthorisedRepresentative &&
    hasAtLeastOneStandardAssigned &&
    isComplianceStudioComplete

  const runGenerate = () => {
    generateDoc.mutate(undefined, {
      onSuccess: () => {
        setDeclarationMarkdownSyncKey((key) => key + 1)
        toast.success(t(`${pageKeys}.generateSuccess`))
      },
      onError: (err) => {
        const message = axios.isAxiosError(err)
          ? (err.response?.data as { message?: string } | undefined)?.message
          : undefined
        toast.error(
          typeof message === 'string' && message.length > 0
            ? message
            : t(`${pageKeys}.generateFailed`)
        )
      },
    })
  }

  const handleGenerateClick = () => {
    if (storedMarkdown != null && storedMarkdown.length > 0) {
      setRegenerateOpen(true)
      return
    }
    runGenerate()
  }

  const handleConfirmRegenerate = () => {
    setRegenerateOpen(false)
    runGenerate()
  }

  const handleSave = async (markdown: string) => {
    try {
      await patchMarkdown.mutateAsync({ markdown })
      toast.success(t(`${pageKeys}.saveSuccess`))
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined
      toast.error(
        typeof message === 'string' && message.length > 0
          ? message
          : t(`${pageKeys}.saveFailed`)
      )
    }
  }

  const handleExportPdf = () => {
    exportPdf.mutate(undefined, {
      onSuccess: () => {
        toast.success(t(`${pageKeys}.pdfExportedSuccess`))
      },
      onError: (err) => {
        toast.error(err.message ?? t(`${pageKeys}.exportFailed`))
      },
    })
  }

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  return (
    <ComplianceDocumentManifestProvider
      documentKind='risk_assessment'
      locale={i18n.language}
    >
      <div className='flex h-full flex-col space-y-4'>
        <ComplianceSubPageHeader>
          <ComplianceSubPageTitle>
            {t(`${pageKeys}.title`)}
          </ComplianceSubPageTitle>
          <div className='flex flex-wrap items-center gap-2'>
            {!hasDocument && (
              <Button
                onClick={handleGenerateClick}
                disabled={!canGenerate || generateDoc.isPending}
              >
                {t(`${pageKeys}.generate`)}
              </Button>
            )}
            {hasDocument && (
              <>
                <Button
                  onClick={handleExportPdf}
                  variant='outline'
                  disabled={exportPdf.isPending}
                >
                  {exportPdf.isPending
                    ? t(`${pageKeys}.exporting`)
                    : t(`${pageKeys}.exportPdf`)}
                </Button>
                <Button
                  onClick={handleGenerateClick}
                  variant='outline'
                  disabled={!canGenerate || generateDoc.isPending}
                >
                  {t(`${pageKeys}.regenerate`)}
                </Button>
              </>
            )}
          </div>
        </ComplianceSubPageHeader>

        {!canGenerate && (
          <DeclarationOfConformityRequirementsChecklist
            isPartlyCompletedMachine={isPartlyCompletedMachine}
            complianceId={complianceId}
            buildHref={buildHref}
            requirements={requirements}
          />
        )}

        {!hasDocument && (
          <Empty className='min-h-[320px] rounded-lg border border-dashed'>
            <EmptyHeader>
              <EmptyMedia variant='icon'>
                <ScrollText className='size-10' />
              </EmptyMedia>
              <EmptyTitle>{t(`${pageKeys}.emptyTitle`)}</EmptyTitle>
              <EmptyDescription>
                {canGenerate
                  ? t(`${pageKeys}.emptyDescription`)
                  : t(`${pageKeys}.emptyDescriptionBlocked`)}
              </EmptyDescription>
            </EmptyHeader>
            {canGenerate && (
              <EmptyContent>
                <Button
                  onClick={handleGenerateClick}
                  disabled={generateDoc.isPending}
                  size='sm'
                  className='gap-2'
                >
                  <ScrollText className='size-4' />
                  {t(`${pageKeys}.generate`)}
                </Button>
              </EmptyContent>
            )}
          </Empty>
        )}

        {hasDocument && (
          <>
            {showClassificationStaleWarning && (
              <Alert variant='warning'>
                <AlertTitle>
                  {t('classificationChangeStaleDocument.title')}
                </AlertTitle>
                <AlertDescription>
                  {t('classificationChangeStaleDocument.description')}
                </AlertDescription>
              </Alert>
            )}
            <DocumentEditor
              contentSyncKey={declarationMarkdownSyncKey}
              markdown={displayMarkdown}
              onSave={handleSave}
              isSaving={patchMarkdown.isPending}
              saveLabel={t(`${pageKeys}.save`)}
              savingLabel={t(`${pageKeys}.saving`)}
              imageUpload={{ complianceId }}
            />
          </>
        )}

        <AlertDialog open={regenerateOpen} onOpenChange={setRegenerateOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t(`${pageKeys}.regenerateConfirmTitle`)}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(`${pageKeys}.regenerateConfirmDescription`)}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t(`${pageKeys}.regenerateCancel`)}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmRegenerate}>
                {t(`${pageKeys}.regenerateConfirm`)}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ComplianceDocumentManifestProvider>
  )
}
